import React from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import AutoBindComponent from "../common/AutoBindComponent";

import { generateGuid } from "../../util/guid";
import mergeDeep from "../../util/mergeDeep";
import Tooltip from "../common/Tooltip";
import * as types from "../../constants/ComponentConstants";
import ValidationMessage from "../common/ValidationMessage";
import Revert from "../common/Revert";

import { Dropdown } from "semantic-ui-react";
import { orderByPropertyName } from "../../util/orderBy";

class AutoComplete extends AutoBindComponent {
	constructor(props, context) {
		super(props, context);
		this.MAXITEMCOUNT = 10;

		this.state = {
			justGotCleared: false,
			search: '',
			items: [],
			focused: false
		};
	}

	componentDidMount() {
		const temp = this.props.items.map(x => mergeDeep({}, x, { id: generateGuid() }));
		this.setState({
			search: '',
			items: temp
		});
	}

	shouldComponentUpdate(nextProps, nextState) {
		if (this.props.items !== nextProps.items || this.state.justGotCleared) {
			this.setState({
				search: '',
				justGotCleared: false,
				items: nextProps.items.map(x => mergeDeep({}, x, { id: generateGuid() }))
			});
		}

		return this.props.value !== nextProps.value
			|| this.state.search !== nextState.search
			|| this.props.validationMessage !== nextProps.validationMessage
			|| this.props.disabled !== nextProps.disabled
			|| this.props.isLoading !== nextProps.isLoading
			|| this.props.items !== nextProps.items
			|| this.state.items !== nextState.items
			|| this.props.orangeBackground !== nextProps.orangeBackground
			|| this.state.focused !== nextState.focused;
	}

	handleOnSearch(e, event) {
		this.setState({ search: event.searchQuery });
		if (this.props.allowFreeText) {
			this.props.onChange({ target: { value: event.searchQuery, name: this.props.name } });
		}
	}

	handleOnChange(e, data) {
		if (this.props.onChange) {
			let event = data ? { target: data } : e;
			event.target.value = event.target.value ? this.state.items.find(x => x.id === event.target.value).value : '';

			this.props.onChange(event, data);

			if (!data.value) {
				this.handleOnSearch(null, { searchQuery: "" });
				if (this.props.onEnter) {
					this.props.onEnter();
				}
			}
			else if (this.props.onEnter && e.type === "click") {
				this.props.onEnter();
			}
		}
	}

	getLabelItemByValue(value, noId) {
		if (!noId) {
			let label = this.props.allowFreeText ? this.props.value : "";
			const item = this.props.items.find(item => item.value == value);
			if (item != null) {
				label = item.label;
			}
			return label;
		}
		return value;
	}

	handleOnBlur() {
		this.setState({ focused: false });
	}

	handleOnFocus = () => {
		this.setState({ focused: true });
	};

	handleOnClose(event) {
		if (event && event.which && event.which === 13) return this.props.onEnter ? this.props.onEnter() : null;
	}

	render() {
		const noId = this.props.hasNoId;
		const hasValidationMessage = !!this.props.validationMessage;
		const validationMessageType = this.props.validationMessageType;
		const tooltiptype = this.props.tooltipType || types.TOOLTIP_INFO;
		const defaultItem = this.props.useDefaultItem ? (this.props.defaultItem ? this.props.defaultItem : "Kies een optie") : null;
		const value = this.props.value ? this.props.value : "";
		const sizeClassName = this.props.size ? "o-col-" + this.props.size : "o-col-12";
		const label = this.props.label + (this.props.optional ? " (Optioneel)" : "");
		const info = this.props.tooltip ? <Tooltip isInput text={this.props.tooltip} tooltipType={tooltiptype} /> : null;
		const validation = this.props.validationMessage ? <ValidationMessage message={this.props.validationMessage} type={validationMessageType} /> : null;
		const icon = 'search';
		const id = generateGuid();
		const noResultsMessage = this.props.noResultsMessage ? this.props.noResultsMessage : 'Tik en zoek';
		let text = "";

		const hasValue = !!value || (!!defaultItem && ((!value && !this.state.search) || !this.justGotCleared)) || !!this.state.search || (this.props.allowFullListSelect && this.state.focused);

		const inputClassName = classnames({
			"c-input__field": true
		});
		const labelClassName = classnames({
			"c-input__label": true,
			"has-value": hasValue
		});

		const containerClassName = classnames({
			"c-input": true,
			"c-input--dropdown": true,
			"has-value": hasValue,
			"error": hasValidationMessage && validationMessageType !== types.ValidationMessageTypes.WARNING,
			"warning": hasValidationMessage && validationMessageType === types.ValidationMessageTypes.WARNING,
			"disabled": this.props.disabled || this.props.isLoading
		});
		if (value) {
			text = this.getLabelItemByValue(value, noId);
		}
		else if (defaultItem) {
			text = defaultItem;
		}
		const revertable = (this.props.disabled !== true && this.props.disabled !== 'true') && this.props.originalValue !== undefined ? this.props.originalValue !== value : false;

		let options = [];
		if (this.props.allowFullListSelect || (this.state.search && this.state.search.length > this.props.minSearchLength)) {
			const searchresults = this.state.items.filter(item => item.label.toLowerCase().search(this.state.search ? this.state.search.toLowerCase() : "") !== -1);

			options = searchresults.map(x => { return { text: x.label, value: x.id, key: x.id }; });

			if (this.props.sort)
				options = orderByPropertyName(options, "value");
		}

		return (
			<div className={sizeClassName}>
				<div className={containerClassName} style={{ backgroundColor: this.props.orangeBackground ? "#ffdb99" : null }}>
					<Dropdown
						id={id}
						className={inputClassName}
						fluid
						text={text}
						selection
						search
						name={this.props.name}
						icon={icon}
						closeOnChange
						options={options}
						placeholder={defaultItem}
						onChange={this.handleOnChange}
						disabled={this.props.disabled || this.props.isLoading}
						loading={this.props.loading}
						onSearchChange={this.handleOnSearch}
						noResultsMessage={noResultsMessage}
						selectOnBlur={!this.props.allowFreeText}
						style={{ minHeight: '63px' }}
						trigger={text ? null : <label className={labelClassName} htmlFor={id}>{label}</label>}
						searchInput={{ autoFocus: this.props.hasFocus }}
						onClose={this.handleOnClose} />
					{!text || <label className={labelClassName} htmlFor={id}>{label}</label>}
					{info}
					{!revertable || <Revert
						onChange={this.handleOnChange}
						name={this.props.name}
						originalValue={this.props.originalValue} />}
					{this.props.isLoading ?
						<div className={"c-reactloader dark"} style={{ top: '-8px', right: '-8px', position: 'absolute' }}>
							<i className="loader" style={{ width: '30px', height: '30px' }}>
							</i>
						</div> : null}
					{this.props.clearable && this.props.value ? <a onClick={() => { this.setState({ justGotCleared: true }, () => this.handleOnChange({}, { value: '', name: this.props.name })); }} style={{ top: '20px', right: '4px', position: 'absolute' }}>
						<i className="fa fa-fw fa-times" style={{ fontWeight: 'lighter', fontSize: '12px' }}></i>
					</a> : null}
				</div>
				{validation}
			</div>
		);
	}
}

AutoComplete.defaultProps = {
	minSearchLength: 0,
	allowFreeText: false,
	disabled: false,
	useDefaultItem: true,
	sort: false
};

AutoComplete.propTypes = {
	name: PropTypes.string.isRequired,
	label: PropTypes.string.isRequired,
	onChange: PropTypes.func.isRequired,
	value: PropTypes.string,
	originalValue: PropTypes.string,
	tooltip: PropTypes.string,
	tooltipType: PropTypes.string,
	validationMessage: PropTypes.string,
	validationMessageType: PropTypes.string,
	disabled: PropTypes.bool,
	size: PropTypes.number,
	optional: PropTypes.bool,
	defaultItem: PropTypes.string,
	useDefaultItem: PropTypes.bool,
	items: PropTypes.array.isRequired,
	loading: PropTypes.bool,
	hasNoId: PropTypes.bool,
	noResultsMessage: PropTypes.string,
	clearable: PropTypes.bool,
	isLoading: PropTypes.bool,
	allowFullListSelect: PropTypes.bool,
	minSearchLength: PropTypes.number,
	allowFreeText: PropTypes.bool,
	orangeBackground: PropTypes.bool,
	sort: PropTypes.bool,
	hasFocus: PropTypes.bool,
	onEnter: PropTypes.func
};

export default AutoComplete;
